// Passport photo task - main component.
//
// This is a multi-step wizard for creating print-ready passport photos.
// All processing happens client-side for privacy.
//

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { StepIndicator } from "@/site/ui/step-indicator";
import { ThemeControl } from "@/theme/theme-control";

// Hook to get public env values.
// Returns empty strings during SSR and initial render to avoid hydration mismatch.
//
function usePublicEnv() {
  const [env, setEnv] = useState({ donationLink: "", siteAuthor: "" });

  useEffect(() => {
    const publicEnv = (
      window as unknown as {
        publicEnv?: { stripeDonationLink?: string; siteAuthor?: string };
      }
    ).publicEnv;
    setEnv({
      donationLink: publicEnv?.stripeDonationLink ?? "",
      siteAuthor: publicEnv?.siteAuthor ?? "",
    });
  }, []);

  return env;
}

import { CropAdjustStep } from "./steps/crop-adjust";
import { PreviewDownloadStep } from "./steps/preview-download";
import { RemoveBackgroundStep } from "./steps/remove-background";
import { SelectSizeStep } from "./steps/select-size";
import { UploadStep } from "./steps/upload";
import type { CropConfig } from "./utils/canvas";
import {
  GAP_CONFIG,
  type OutputSize,
  type PhotoSize,
} from "./utils/dimensions";

// Step definitions for the indicator.
//
const STEPS_WITH_BACKGROUND_REMOVAL = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Remove BG" },
  { id: 3, label: "Adjust" },
  { id: 4, label: "Size" },
  { id: 5, label: "Download" },
];

const STEPS_WITHOUT_BACKGROUND_REMOVAL = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Adjust" },
  { id: 3, label: "Size" },
  { id: 4, label: "Download" },
];

const BACKGROUND_REMOVAL_STEP = 2;

// Individual photo item with its own crop config.
//
export interface PhotoItem {
  id: string;
  originalImage: Blob;
  backgroundRemovedImage: Blob | null;
  croppedImage: Blob | null;
  cropConfig: CropConfig;
  backgroundRemoved: boolean;
}

// Generate unique ID for photos.
//
let photoIdCounter = 0;
function generatePhotoId(): string {
  return `photo-${Date.now()}-${photoIdCounter++}`;
}

// Task state shared between steps.
//
interface TaskState {
  photos: PhotoItem[];
  activePhotoIndex: number;
  photoSize: PhotoSize;
  removeBackground: boolean;
  outputSize: OutputSize;
  photoGap: number;
  tiledImage: Blob | null;
}

const initialState: TaskState = {
  photos: [],
  activePhotoIndex: 0,
  photoSize: "2x2",
  removeBackground: true,
  outputSize: "4x6",
  photoGap: GAP_CONFIG.default,
  tiledImage: null,
};

// Passport photo task component.
//
export default function PassportTask() {
  const [currentStep, setCurrentStep] = useState(1);
  const [state, setState] = useState<TaskState>(initialState);
  const [isComplete, setIsComplete] = useState(false);
  const { donationLink, siteAuthor } = usePublicEnv();
  const steps = state.removeBackground
    ? STEPS_WITH_BACKGROUND_REMOVAL
    : STEPS_WITHOUT_BACKGROUND_REMOVAL;
  const cropStep = state.removeBackground ? 3 : 2;
  const sizeStep = cropStep + 1;
  const downloadStep = sizeStep + 1;
  const maxStep = steps.length;

  // Step navigation handlers.
  //
  const goNext = () => {
    if (currentStep < maxStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Return to upload so the photo-size selector remains reachable.
  //
  const goBackToUpload = () => {
    setCurrentStep(1);
  };

  // Add new photos to state.
  //
  const handleUpload = (files: File[]) => {
    const newPhotos: PhotoItem[] = files.map((file) => ({
      id: generatePhotoId(),
      originalImage: new Blob([file], { type: file.type }),
      backgroundRemovedImage: null,
      croppedImage: null,
      cropConfig: { x: 0, y: 0, zoom: 1 },
      backgroundRemoved: false,
    }));
    setState((prev) => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos],
      activePhotoIndex: prev.photos.length, // Select first new photo
    }));
  };

  // Continue from upload step to crop step.
  //
  const handleUploadContinue = () => {
    if (state.photos.length > 0) {
      goNext();
    }
  };

  // Add more photos from crop or preview step.
  //
  const handleAddMorePhotos = (files: File[]) => {
    const newPhotos: PhotoItem[] = files.map((file) => ({
      id: generatePhotoId(),
      originalImage: new Blob([file], { type: file.type }),
      backgroundRemovedImage: null,
      croppedImage: null,
      cropConfig: { x: 0, y: 0, zoom: 1 },
      backgroundRemoved: false,
    }));
    setState((prev) => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos],
      activePhotoIndex: prev.photos.length, // Select first new photo
    }));
    setCurrentStep(state.removeBackground ? BACKGROUND_REMOVAL_STEP : cropStep);
  };

  // Remove a photo by ID.
  //
  const handleRemovePhoto = (photoId: string) => {
    setState((prev) => {
      const newPhotos = prev.photos.filter((p) => p.id !== photoId);
      const newActiveIndex = Math.min(
        prev.activePhotoIndex,
        newPhotos.length - 1,
      );
      return {
        ...prev,
        photos: newPhotos,
        activePhotoIndex: Math.max(0, newActiveIndex),
      };
    });
  };

  // Set which photo is being edited.
  //
  const handleSetActivePhoto = (index: number) => {
    setState((prev) => ({ ...prev, activePhotoIndex: index }));
  };

  // Background removal handler (for full flow).
  // Receives a Map of photo ID -> transparent blob.
  //
  const handleBackgroundRemoved = (processedPhotos: Map<string, Blob>) => {
    setState((prev) => {
      const newPhotos = prev.photos.map((photo) => {
        const transparentBlob = processedPhotos.get(photo.id);
        if (transparentBlob) {
          return {
            ...photo,
            backgroundRemovedImage: transparentBlob,
            backgroundRemoved: true,
          };
        }
        return photo;
      });
      return { ...prev, photos: newPhotos };
    });
    goNext();
  };

  // Complete crop for a photo (by ID, or active photo if no ID provided).
  //
  const handleCropComplete = (
    croppedBlob: Blob,
    config: CropConfig,
    photoId?: string,
  ) => {
    setState((prev) => {
      const newPhotos = [...prev.photos];
      const targetIndex = photoId
        ? newPhotos.findIndex((p) => p.id === photoId)
        : prev.activePhotoIndex;
      const targetPhoto = newPhotos[targetIndex];
      if (targetPhoto) {
        newPhotos[targetIndex] = {
          ...targetPhoto,
          croppedImage: croppedBlob,
          cropConfig: config,
        };
      }
      return { ...prev, photos: newPhotos };
    });
  };

  // Continue to next step from crop.
  //
  const handleCropContinue = () => {
    goNext();
  };

  // Update the target dimensions and clear crops made for the old aspect ratio.
  //
  const handlePhotoSizeChange = (photoSize: PhotoSize) => {
    setState((prev) => {
      if (prev.photoSize === photoSize) return prev;

      return {
        ...prev,
        photoSize,
        photos: prev.photos.map((photo) => ({
          ...photo,
          croppedImage: null,
          cropConfig: { x: 0, y: 0, zoom: 1 },
        })),
        tiledImage: null,
      };
    });
  };

  // Switch image sources and clear crops made from the previous source.
  // Processed backgrounds stay cached in case removal is re-enabled.
  //
  const handleBackgroundRemovalChange = (removeBackground: boolean) => {
    setState((prev) => {
      if (prev.removeBackground === removeBackground) return prev;

      return {
        ...prev,
        removeBackground,
        photos: prev.photos.map((photo) => ({
          ...photo,
          croppedImage: null,
          cropConfig: { x: 0, y: 0, zoom: 1 },
        })),
        tiledImage: null,
      };
    });
  };

  const handleSizeSelected = (size: OutputSize) => {
    setState((prev) => ({
      ...prev,
      outputSize: size,
      tiledImage: null,
    }));
    goNext();
  };

  const handleTiledImageReady = (tiledBlob: Blob) => {
    setState((prev) => ({ ...prev, tiledImage: tiledBlob }));
  };

  // Reset to start over.
  //
  const handleStartOver = () => {
    setState(initialState);
    setCurrentStep(1);
    setIsComplete(false);
  };

  // Mark task as complete after download.
  //
  const handleDownloadComplete = () => {
    setIsComplete(true);
  };

  const photosNeedingBackgroundRemoval = useMemo(() => {
    return state.photos.filter((photo) => !photo.backgroundRemoved);
  }, [state.photos]);

  useEffect(() => {
    if (
      state.removeBackground &&
      currentStep === BACKGROUND_REMOVAL_STEP &&
      state.photos.length > 0 &&
      photosNeedingBackgroundRemoval.length === 0
    ) {
      setCurrentStep(cropStep);
    }
  }, [
    cropStep,
    currentStep,
    photosNeedingBackgroundRemoval.length,
    state.photos.length,
    state.removeBackground,
  ]);

  // Get cropped images for tiling (memoized to prevent re-renders).
  //
  const croppedImages = useMemo((): Blob[] => {
    return state.photos.reduce<Blob[]>((images, photo) => {
      if (photo.croppedImage) {
        images.push(photo.croppedImage);
      }

      return images;
    }, []);
  }, [state.photos]);

  // Render the current step.
  //
  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <UploadStep
          photos={state.photos}
          photoSize={state.photoSize}
          removeBackground={state.removeBackground}
          onBackgroundRemovalChange={handleBackgroundRemovalChange}
          onContinue={handleUploadContinue}
          onPhotoSizeChange={handlePhotoSizeChange}
          onRemovePhoto={handleRemovePhoto}
          onUpload={handleUpload}
        />
      );
    }

    if (state.removeBackground && currentStep === BACKGROUND_REMOVAL_STEP) {
      return photosNeedingBackgroundRemoval.length > 0 ? (
        <RemoveBackgroundStep
          photos={photosNeedingBackgroundRemoval}
          onBack={goBack}
          onComplete={handleBackgroundRemoved}
        />
      ) : null;
    }

    if (currentStep === cropStep) {
      return (
        <CropAdjustStep
          activePhotoIndex={state.activePhotoIndex}
          photos={state.photos}
          photoSize={state.photoSize}
          removeBackground={state.removeBackground}
          onAddMorePhotos={handleAddMorePhotos}
          onBack={goBackToUpload}
          onContinue={handleCropContinue}
          onCropComplete={handleCropComplete}
          onRemovePhoto={handleRemovePhoto}
          onSetActivePhoto={handleSetActivePhoto}
        />
      );
    }

    if (currentStep === sizeStep) {
      return (
        <SelectSizeStep
          initialSize={state.outputSize}
          photoSize={state.photoSize}
          onBack={goBack}
          onContinue={handleSizeSelected}
        />
      );
    }

    if (currentStep === downloadStep) {
      return (
        <PreviewDownloadStep
          croppedImages={croppedImages}
          outputSize={state.outputSize}
          photoGap={state.photoGap}
          photoSize={state.photoSize}
          onAddMorePhotos={handleAddMorePhotos}
          onBack={goBack}
          onDownloadComplete={handleDownloadComplete}
          onStartOver={handleStartOver}
          onTiledImageReady={handleTiledImageReady}
        />
      );
    }

    return null;
  };

  return (
    <div className="mx-auto flex min-h-dvh w-[calc(100%-3rem)] max-w-3xl flex-col">
      {/* Header */}
      <header className="flex h-14 items-center justify-end">
        <ThemeControl />
      </header>

      {/* Main content */}
      <main className="flex flex-1 flex-col">
        {/* Title and description */}
        <h1 className="mt-8 text-2xl font-semibold sm:mt-16 sm:text-3xl">
          Passport Photo Creator
        </h1>
        <p className="text-muted-foreground mt-1 sm:font-medium">
          Create print-ready passport photos privately in your browser.
        </p>

        <p className="text-muted-foreground my-5 text-sm sm:my-10">
          All processing happens locally on your device. Your photos are never
          uploaded to any server.
        </p>
        <p className="text-muted-foreground -mt-3 mb-5 text-xs sm:-mt-8 sm:mb-10">
          Limited, cookie-free audience measurement may be enabled to improve
          this tool. It excludes URL searches and fragments, respects Do Not
          Track, never receives your photos or form values, and does not record
          session replays.
        </p>

        {/* Step indicator */}
        <StepIndicator
          className="mb-8"
          currentStep={currentStep}
          isComplete={isComplete}
          steps={steps}
        />

        {/* Step content */}
        <div className="rounded-xl border p-6">{renderStep()}</div>
      </main>

      {/* Footer */}
      <footer className="text-muted-foreground [&_a]:underline-decoration mt-10 flex flex-col items-center gap-2 border-t py-5 text-sm sm:mt-15 [&_a]:underline">
        <nav aria-label="Legal" className="flex gap-4">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </nav>
        {donationLink && (
          <p>
            Find this useful?{" "}
            <a href={donationLink} rel="noopener noreferrer" target="_blank">
              Support this project
            </a>
          </p>
        )}
        {siteAuthor && (
          <p>
            Built by <Link to="https://quietcolin.com">{siteAuthor}</Link>.
          </p>
        )}
      </footer>
    </div>
  );
}
